"use client";

import { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { PROVINCES, type Province } from "@/data/provinces";

type Props = {
  defaultProvince?: string; // es: "Milano"
  placeholder?: string;
  ctaLabel?: string;
  onSubmit?: (value: string) => void;
};

function normalize(s: string) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

function useInputRect(ref: React.RefObject<HTMLInputElement>, open: boolean) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    function update() {
      if (ref.current) setRect(ref.current.getBoundingClientRect());
    }
    if (open) {
      update();
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
    }
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [ref, open]);
  return rect;
}

export default function HeroProvinceBar({
  defaultProvince,
  placeholder = "Provincia o comune",
  ctaLabel = "CERCA",
  onSubmit,
}: Props) {
  const router = useRouter();
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  const provinces: Province[] = useMemo(() => {
    const arr = (PROVINCES as Province[]).filter(Boolean);
    const map = new Map<string, Province>();
    for (const p of arr) if (p?.slug && p?.name && !map.has(p.slug)) map.set(p.slug, p);
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "it"));
  }, []);

  // UI state
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("Vicino a me");
  const [selected, setSelected] = useState<Province | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isFirstFocus, setIsFirstFocus] = useState(true); // Track if it's the first focus with "Vicino a me"

  // Default selection
  useEffect(() => {
    if (!defaultProvince) {
      setSelected(null);
      setQuery("Vicino a me");
      return;
    }
    const def =
      provinces.find((p) => normalize(p.name) === normalize(defaultProvince)) ||
      provinces.find((p) => normalize(p.slug) === normalize(defaultProvince)) ||
      provinces.find((p) => normalize(p.sigla) === normalize(defaultProvince)) ||
      provinces[0] ||
      null;
    setSelected(def);
    setQuery(def ? def.name : "");
  }, [provinces, defaultProvince]);

  // ESC to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery(selected ? selected.name : "Vicino a me");
        // Reset first focus when closing with ESC if query is reset to "Vicino a me"
        if (!selected) {
          setIsFirstFocus(true);
        }
        inputRef.current?.blur();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, selected]);

  // Filter (diacritics-insensitive) with tiny debounce
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 80);
    return () => clearTimeout(t);
  }, [query]);

  const filtered = useMemo(() => {
    const q = normalize(debouncedQuery);
    const isVicinoAMe = q === normalize("Vicino a me");
    
    // If query is empty and dropdown is open, show all provinces
    if (!q && open) return provinces;
    // If query is empty and dropdown is closed, return empty array
    if (!q) return [];
    
    // If it's the first focus and query is "Vicino a me", show all provinces
    if (isVicinoAMe && isFirstFocus && open) return provinces;
    
    // Otherwise, filter provinces based on query
    return provinces.filter((p) => {
      const hay = [
        normalize(p.name),
        normalize(p.sigla),
        normalize(p.regioneName),
        normalize(p.regioneSlug),
      ].join(" ");
      return hay.includes(q);
    });
  }, [provinces, debouncedQuery, open, isFirstFocus]);

  // Keep activeIndex within bounds
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    // Reset to "Vicino a me" when opening
    setActiveIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Total items = 1 (Vicino a me) + filtered.length
    const totalItems = 1 + filtered.length;
    if (totalItems === 1) {
      // Only "Vicino a me" is available
      setActiveIndex(0);
      return;
    }
    // reset active to first if out of range
    if (activeIndex < 0 || activeIndex >= totalItems) setActiveIndex(0);
  }, [filtered.length, activeIndex, open]);

  function choose(p: Province) {
    setSelected(p);
    setQuery(p.name);
    setOpen(false);
    inputRef.current?.focus();
    // Redirect automatico alla provincia selezionata
    router.push(`/sagre/${p.regioneSlug}/${p.slug}/`);
  }

  function chooseNearMe() {
    setOpen(false);
    inputRef.current?.focus();
    router.push("/vicino-a-me");
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();

    // Set flag to prevent dropdown from opening during submit
    isSubmittingRef.current = true;
    
    // Close dropdown immediately to prevent it from opening on focus
    setOpen(false);

    // Reset flag after a short delay to allow navigation to complete
    setTimeout(() => {
      isSubmittingRef.current = false;
    }, 100);

    // If custom onSubmit is provided, use it
    if (onSubmit) {
      onSubmit(query);
      return;
    }

    // Check if "Vicino a me" is selected or if query is "Vicino a me"
    const normalizedQuery = normalize(query);
    if (normalizedQuery === normalize("Vicino a me")) {
      chooseNearMe();
      return;
    }

    // Default behavior: Prefer selected, else first filtered
    const target = selected ?? filtered[0];
    if (!target) return;
    router.push(`/sagre/${target.regioneSlug}/${target.slug}/`);
    setQuery(selected ? selected.name : ""); // restore last selected in the bar
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setQuery(""); // show full list on open via keyboard
      setOpen(true);
      return;
    }
    if (!open) return;

    // Total items = 1 (Vicino a me) + filtered.length
    const totalItems = 1 + filtered.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min((i < 0 ? -1 : i) + 1, totalItems - 1));
      scrollActiveIntoView();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max((i < 0 ? totalItems : i) - 1, 0));
      scrollActiveIntoView();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex === 0) {
        // "Vicino a me" is selected
        chooseNearMe();
      } else if (activeIndex > 0 && filtered[activeIndex - 1]) {
        // Province is selected (activeIndex - 1 because "Vicino a me" is at index 0)
        const p = filtered[activeIndex - 1];
        choose(p);
      } else {
        // No valid selection, default to "Vicino a me"
        chooseNearMe();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  function scrollActiveIntoView() {
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      // For "Vicino a me" (index 0), use the special data attribute
      // For provinces, use activeIndex - 1
      const dataIndex = activeIndex === 0 ? "nearme" : (activeIndex - 1).toString();
      const el = list.querySelector<HTMLElement>(`[data-index="${dataIndex}"]`);
      el?.scrollIntoView({ block: "nearest" });
    });
  }

  const listboxId = "wherebar-province-listbox";
  const activeOptionId = activeIndex >= 0 ? `wherebar-option-${activeIndex}` : undefined;
  const inputRect = useInputRect(inputRef, open);

  try {
    return (
    <form className="wherebar" onSubmit={handleSubmit} aria-label="Seleziona provincia">
      <div className="wherebar-inner" ref={wrapRef}>
        {/* Icona pin */}
        <div className="wherebar-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1116 0Zm-8 2a2 2 0 100-4 2 2 0 000 4Z"
            />
          </svg>
        </div>

        {/* INPUT: combobox */}
        <input
          ref={inputRef}
          id="wherebar-province-input"
          type="text"
          className="wherebar-trigger wherebar-input"
          placeholder={placeholder}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-label="Cerca e seleziona una provincia"
          value={query}
          onChange={(e) => {
            const newValue = e.target.value;
            // If current query is "Vicino a me" and user is modifying it, replace completely
            if (query === "Vicino a me" && newValue !== "Vicino a me") {
              // User started typing or deleting - replace "Vicino a me" completely
              // Remove "Vicino a me" prefix if present, otherwise use newValue as is
              if (newValue.startsWith("Vicino a me")) {
                // User typed after "Vicino a me" - remove the prefix and keep only what they typed
                const typedPart = newValue.substring("Vicino a me".length).trim();
                setQuery(typedPart || ""); // Use empty string if only whitespace
              } else {
                // User deleted or replaced - use the new value directly
                setQuery(newValue);
              }
            } else {
              setQuery(newValue);
            }
            setOpen(true);
            // When user starts typing, disable first focus behavior
            setIsFirstFocus(false);
          }}
          onFocus={() => {
            // Don't open dropdown if we're in the middle of submitting
            if (isSubmittingRef.current) return;
            setOpen(true);
            // Check if query is still "Vicino a me" to determine if we should show all provinces
            const normalizedQuery = normalize(query);
            if (normalizedQuery === normalize("Vicino a me")) {
              // Keep first focus behavior if it's still "Vicino a me"
              // This allows showing all provinces on first tap
            } else {
              // User has modified the query, disable first focus behavior
              setIsFirstFocus(false);
            }
          }}
          onKeyDown={(e) => {
            // If query is "Vicino a me" and user starts typing or deleting, replace completely
            if (query === "Vicino a me") {
              // Check if it's a printable character (not modifier keys, arrows, etc.)
              const isPrintableChar = e.key.length === 1 && 
                !e.ctrlKey && 
                !e.metaKey && 
                !e.altKey &&
                e.key !== "Enter" &&
                e.key !== "Escape" &&
                e.key !== "ArrowDown" &&
                e.key !== "ArrowUp" &&
                e.key !== "ArrowLeft" &&
                e.key !== "ArrowRight" &&
                e.key !== "Tab";
              
              if (isPrintableChar || e.key === "Backspace" || e.key === "Delete") {
                e.preventDefault();
                if (e.key === "Backspace" || e.key === "Delete") {
                  // Clear completely
                  setQuery("");
                } else {
                  // Replace with the typed character
                  setQuery(e.key);
                }
                setIsFirstFocus(false);
                setOpen(true);
                // Position cursor at the end
                requestAnimationFrame(() => {
                  if (inputRef.current) {
                    const len = inputRef.current.value.length;
                    inputRef.current.setSelectionRange(len, len);
                  }
                });
                return;
              }
            }
            // Handle other keys (arrows, Enter, etc.)
            onKeyDown(e);
          }}
          autoComplete="off"
        />

        {/* Bottone 'Cerca' (desktop) */}
        <button
          type="submit"
          className="wherebar-search wherebar-search-desktop"
          aria-label="Cerca sagre nella provincia selezionata"
          onMouseDown={(e) => {
            // Prevent input from receiving focus when clicking the button
            e.preventDefault();
            isSubmittingRef.current = true;
          }}
          onClick={(e) => {
            // Ensure dropdown stays closed
            setOpen(false);
          }}
        >
          {ctaLabel}
        </button>

        {/* Dropdown risultati - Portal */}
        {open &&
          inputRect &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className="wherebar-layer"
              aria-hidden={false}
              onClick={(e) => {
                // close when clicking outside popup
                // allow clicks inside popup
                const el = e.target as HTMLElement;
                if (el.closest(".wherebar-popup")) return;
                setOpen(false);
                setQuery(selected ? selected.name : "Vicino a me");
                // Reset first focus when closing if query is reset to "Vicino a me"
                if (!selected) {
                  setIsFirstFocus(true);
                }
              }}
            >
              <div
                className="wherebar-popup"
                style={{
                  position: "fixed",
                  top: inputRect.bottom + 18,
                  left: inputRect.left - 32,
                  width: inputRect.width + 40,
                }}
                role="presentation"
              >
                <ul
                  ref={listRef}
                  className="wherebar-dropdown"
                  role="listbox"
                  id={listboxId}
                  aria-label="Province"
                >
                  {/* "Vicino a me" - sempre primo elemento */}
                  <li
                    id="wherebar-option-0"
                    data-index="nearme"
                    role="option"
                    aria-selected={activeIndex === 0}
                    className={`wherebar-option wherebar-option-nearme ${activeIndex === 0 ? "is-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={chooseNearMe}
                  >
                    <div className="wherebar-option-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="currentColor"
                          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                        />
                        <circle cx="12" cy="9" r="2" fill="currentColor" opacity="0.3" />
                        <circle cx="12" cy="9" r="1" fill="currentColor" />
                      </svg>
                    </div>
                    <span className="wherebar-option-label">Vicino a me</span>
                    <div className="wherebar-option-arrow">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="currentColor"
                          d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                        />
                      </svg>
                    </div>
                  </li>

                  {/* Separatore */}
                  {filtered.length > 0 && (
                    <li className="wherebar-separator" role="separator" aria-hidden="true">
                      <span className="wherebar-separator-text">Suggerimenti</span>
                    </li>
                  )}

                  {/* Province */}
                  {filtered.length === 0 && query && (
                    <li
                      className="wherebar-option"
                      role="option"
                      aria-selected="false"
                      aria-disabled="true"
                    >
                      Nessun risultato
                    </li>
                  )}
                  {filtered.map((p, i) => {
                    const isActive = i + 1 === activeIndex; // +1 because "Vicino a me" is at index 0
                    const isSelected = selected?.slug === p.slug;
                    return (
                      <li
                        key={p.slug}
                        id={`wherebar-option-${i + 1}`}
                        data-index={i}
                        role="option"
                        aria-selected={isActive || isSelected}
                        className={`wherebar-option ${isActive ? "is-active" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => choose(p)}
                      >
                        <div className="wherebar-option-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1116 0Zm-8 2a2 2 0 100-4 2 2 0 000 4Z"
                            />
                          </svg>
                        </div>
                        <span className="wherebar-option-label">{p.name}</span>
                        <span className="wherebar-option-meta">
                          {p.regioneSlug.toLowerCase()} {p.sigla ? `· ${p.sigla}` : ""}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Bottone 'Cerca' (mobile) */}
      <button
        type="submit"
        className="wherebar-search wherebar-search-mobile"
        aria-label="Cerca sagre nella provincia selezionata"
        onMouseDown={(e) => {
          // Prevent input from receiving focus when clicking the button
          e.preventDefault();
          isSubmittingRef.current = true;
        }}
        onClick={(e) => {
          // Ensure dropdown stays closed
          setOpen(false);
        }}
      >
        CERCA
      </button>
    </form>
  );
  } catch (error) {
    console.error("HeroProvinceBar error:", error);
    // Return minimal UI structure (matches original to avoid layout shifts)
    return (
      <form className="wherebar" onSubmit={(e) => e.preventDefault()} aria-label="Seleziona provincia">
        <div className="wherebar-inner" ref={wrapRef}>
          <div className="wherebar-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1116 0Zm-8 2a2 2 0 100-4 2 2 0 000 4Z"
              />
            </svg>
          </div>
          <input
            type="text"
            className="wherebar-trigger wherebar-input"
            placeholder={placeholder}
            disabled
            aria-label="Cerca e seleziona una provincia"
          />
          <button type="submit" className="wherebar-search wherebar-search-desktop" aria-label="Cerca sagre nella provincia selezionata">
            {ctaLabel}
          </button>
        </div>
        <button type="submit" className="wherebar-search wherebar-search-mobile" aria-label="Cerca sagre nella provincia selezionata">
          CERCA
        </button>
      </form>
    );
  }
}
